import { staggerGamesForCatalogGrid } from "@/lib/catalog-grid-stagger";

export {
  CATALOG_LARGE_MAX_RANK,
  CATALOG_MEDIUM_MAX_RANK,
  DEFAULT_LARGE_TIER_COUNT,
  DEFAULT_MEDIUM_TIER_COUNT,
  gridIconSizeForCatalogRank,
} from "@/lib/catalog-ranking/tiers";

type CatalogSortableGame = {
  slug: string;
  sort_order?: number | null;
  name?: string | null;
};

/**
 * Sort catalog grid games by refreshed `sort_order`, then name.
 */
export function sortGamesForCatalogGrid<T extends CatalogSortableGame>(games: T[]): T[] {
  return [...games].sort((a, b) => compareGamesForCatalogGrid(a, b));
}

function compareGamesForCatalogGrid(a: CatalogSortableGame, b: CatalogSortableGame): number {
  const sortOrderA = a.sort_order ?? Number.MAX_SAFE_INTEGER;
  const sortOrderB = b.sort_order ?? Number.MAX_SAFE_INTEGER;
  if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;

  return (a.name ?? a.slug).localeCompare(b.name ?? b.slug, undefined, { sensitivity: "base" });
}

/**
 * Popularity order from DB, then size staggering for masonry discovery layout.
 *
 * Games with `grid_override === true` are pinned to their persisted `sort_order`
 * slot (1-indexed). Only the remaining games are passed through the size-pattern
 * stagger and then dropped into whichever slots overrides did not claim, so an
 * admin's manual placement and size are honored exactly.
 */
export function prepareGamesForCatalogGrid<
  T extends CatalogSortableGame & {
    id?: string;
    size?: number | null;
    grid_override?: boolean | null;
    grid_hidden?: boolean | null;
  },
>(games: T[]): T[] {
  const visible = games.filter((game) => game.grid_hidden !== true);
  const sorted = sortGamesForCatalogGrid(visible);
  const overrides = sorted.filter((game) => game.grid_override === true);
  if (overrides.length === 0) {
    return staggerGamesForCatalogGrid(sorted);
  }

  const overrideIds = new Set(overrides.map((game) => game.id ?? game.slug));
  const nonOverrides = sorted.filter((game) => !overrideIds.has(game.id ?? game.slug));
  const staggered = staggerGamesForCatalogGrid(nonOverrides);

  const result: T[] = new Array(sorted.length);
  const claimed = new Set<number>();

  for (const game of overrides) {
    const slot = (game.sort_order ?? 0) - 1;
    if (slot >= 0 && slot < result.length && !claimed.has(slot)) {
      result[slot] = game;
      claimed.add(slot);
    }
  }

  let cursor = 0;
  for (const game of staggered) {
    while (cursor < result.length && claimed.has(cursor)) cursor += 1;
    if (cursor >= result.length) break;
    result[cursor] = game;
    cursor += 1;
  }

  // Any leftover overrides that could not claim a valid slot get appended.
  for (const game of overrides) {
    if (!result.includes(game)) result.push(game);
  }
  return result.filter((game): game is T => game !== undefined);
}
